import * as React from 'react';
import { IconButton, Box, Typography } from '@mui/material';
import HelpTooltip from './HelpTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAppSelector } from '../appHooks';
import { RootState } from '../store';
import HelpDictionary, { HelpContent } from './HelpContent';

interface HelpIconProps {
    helpContext: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
    size?: 'small' | 'medium' | 'large';
    floatingPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

/**
 * HelpIcon Component
 * 
 * Displays a small info icon that shows help content in a tooltip when hovered.
 * Visibility is controlled by the global help visibility state in Redux.
 * 
 * @param helpContext - The key to lookup help content in HelpDictionary
 * @param placement - Tooltip placement (default: 'right')
 * @param size - Icon size (default: 'small')
 * @param floatingPosition - Optional absolute positioning (top-right, top-left, bottom-right, bottom-left)
 */
const HelpIcon: React.FC<HelpIconProps> = ({ 
    helpContext, 
    placement = 'right',
    size = 'small',
    floatingPosition
}) => {
    const isHelpVisible = useAppSelector((state: RootState) => state.persistedHelpVisibilityReducer.isVisible);
    const helpContent: HelpContent | undefined = HelpDictionary.get(helpContext);

    // Don't render if help is not visible or content not found
    if (!isHelpVisible || !helpContent) {
        return null;
    }

    const iconSize = size === 'small' ? 16 : size === 'medium' ? 20 : 24;

    // Calculate absolute positioning styles if floatingPosition is provided
    const getFloatingStyles = (): React.CSSProperties => {
        if (!floatingPosition) return {};
        
        const offset = 4; // pixels from edge
        const baseStyles: React.CSSProperties = {
            position: 'absolute',
            zIndex: 1
        };

        switch (floatingPosition) {
            case 'top-left':
                return { ...baseStyles, top: offset, left: offset };
            case 'bottom-right':
                return { ...baseStyles, bottom: offset, right: offset };
            case 'bottom-left':
                return { ...baseStyles, bottom: offset, left: offset };
            case 'top-right':
            default:
                return { ...baseStyles, top: offset, right: offset };
        }
    };

    return (
        <HelpTooltip
            title={
                <Box sx={{ maxWidth: 400 }}>
                    {/* Help badge header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 1,
                        pb: 0.75,
                        borderBottom: '1px solid rgba(100, 181, 246, 0.35)',
                    }}>
                        <HelpOutlineIcon sx={{ fontSize: 14, color: '#90caf9', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{
                            color: '#90caf9',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            lineHeight: 1,
                        }}>
                            &nbsp;
                        </Typography>
                        {helpContent.title && (
                            <>
                                <Box component="span" sx={{ color: 'rgba(144,202,249,0.4)', mx: 0.25 }}>·</Box>
                                <Typography variant="caption" sx={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    lineHeight: 1,
                                }}>
                                    {helpContent.title}
                                </Typography>
                            </>
                        )}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', lineHeight: 1.55, color: 'rgba(255,255,255,0.87)' }}>
                        {helpContent.content}
                    </Box>
                </Box>
            }
            placement={placement}
        >
            <IconButton
                size={size}
                sx={{
                    ml: floatingPosition ? 0 : 0.5,
                    padding: 0.25,
                    opacity: 0.7,
                    '&:hover': {
                        opacity: 1,
                        backgroundColor: 'action.hover'
                    },
                    ...getFloatingStyles()
                }}
                disableRipple
            >
                <InfoOutlinedIcon sx={{ fontSize: iconSize, color: 'info.main' }} />
            </IconButton>
        </HelpTooltip>
    );
};

export default HelpIcon;
